import { useEffect } from 'react';
import { Microscope, Network, Database, FlaskConical, Search, Mail } from 'lucide-react';
import styles from './Main.module.css';

const features = [
  { icon: Microscope, label: 'Expression atlases', text: 'Microarray and RNA-Seq across different transcriptomic datasets.' },
  { icon: Network, label: 'Co-expression networks', text: 'Aggregated and inferred gene regulatory networks.' },
  { icon: Database, label: 'Gene catalogues', text: 'Curated functions, orthologues and reference cards.' },
  { icon: FlaskConical, label: 'Omics integration', text: 'DAP-Seq, metabolite correlations and more.' },
];

const citations = [
  {
    tag: 'Referencing PlantaeViz',
    featured: true,
    authors:
      'Santiago, A., Orduña, L., Fernández, J. D., Vidal, Á., De Martín-Agirre, I., Lisón, P., Vidal, E. A., Navarro-Payá, D., & Matus, J. T. (2024).',
    title:
      'The Plantae Visualization Platform: A comprehensive web-based tool for the integration, visualization, and analysis of omic data across plant and related species.',
    journal: 'bioRxiv.',
    doi: 'https://doi.org/10.1101/2024.12.19.629382',
  },
  {
    tag: 'Gene Catalogue & Gene Cards',
    authors:
      'Navarro-Payá, D., Santiago, A., Orduña, L., Zhang, C., Amato, A., D’Inca, E., Fattorini, C., Pezzotti, M., Tornielli, G. B., Zenoni, S., Rustenholz, C., & Matus, J. T. (2022).',
    title:
      'The Grape Gene Reference Catalogue as a Standard Resource for Gene Selection and Genetic Improvement.',
    journal: 'Frontiers in Plant Science, 12, 803977.',
    doi: 'https://doi.org/10.3389/fpls.2021.803977',
  },
  {
    tag: 'AggGCNs',
    authors:
      'Orduña, L., Santiago, A., Navarro-Payá, D., Zhang, C., Wong, D. C. J., & Matus, J. T. (2023).',
    title:
      'Aggregated gene co-expression networks predict transcription factor regulatory landscapes in grapevine.',
    journal: 'Journal of Experimental Botany, erad344.',
    doi: 'https://doi.org/10.1093/jxb/erad344',
  },
  {
    tag: 'TransMetaDB',
    authors: 'Savoi, S., Santiago, A., Orduña, L., & Matus, J. T. (2022).',
    title:
      'Transcriptomic and metabolomic integration as a resource in grapevine to study fruit metabolite quality traits.',
    journal: 'Frontiers in Plant Science, 16.',
    doi: 'https://www.frontiersin.org/articles/10.3389/fpls.2022.937927',
  },
  {
    tag: 'GRNs',
    authors: 'Fernández, J. D., Navarro-Payá, D., Santiago, A., Cerda, A., Canan, J., Contreras-Riquelme, S., Moyano, T. C., Landaeta-Sepúlveda, D., Melet, L., Canales, J., Johnson, N. R., Álvarez, J. M., Matus, J. T., & Vidal, E. A. (2025).',
    title: 'Organ-level gene-regulatory networks inferred from transcriptomic data reveal context-specific regulation and highlight novel regulators of ripening and ABA-mediated responses in tomato.',
    journal: 'Plant Communications, 6(11), 101499.',
    doi: 'https://doi.org/10.1016/j.xplc.2025.101499',
  },
  {
    tag: 'Orthology',
    authors: 'Navarro-Payá D., Santiago A., Velt A., Moretto M., Rustenholz C, & Matus J. T. (2026).',
    title: 'AEGIS: an annotation extraction and genomic integration resource.',
    journal: 'Bioinformatics, btag363.',
    doi: 'https://doi.org/10.1093/bioinformatics/btag363',
  }
];

const featuredCitations = citations.filter((c) => c.featured);
const restCitations = citations.filter((c) => !c.featured);

const acknowledgements = [
  { src: '/plantaeviz/acknowledgements/ministerio_ciencia_innovacion_universidades.png', alt: 'Ministerio de Ciencia, Innovación y Universidades', h: 56 },
  { src: '/plantaeviz/acknowledgements/generalitat_valenciana.png', alt: 'Generalitat Valenciana', h: 48 },
  { src: '/plantaeviz/acknowledgements/iec.png', alt: 'IEC', h: 56 },
  { src: '/plantaeviz/acknowledgements/biogenoma.png', alt: 'Biogenoma', h: 48 },
];

const CitationCard = ({ tag, authors, title, journal, doi, featured }) => (
  <article
    className={`rounded-2xl border bg-white p-6 shadow-soft transition-all hover:-translate-y-1 hover:shadow-lift ${
      featured ? 'border-moss-700/40 ring-1 ring-moss-700/20' : 'border-moss-200/70'
    }`}
  >
    <span className="inline-block rounded-full bg-moss-100 px-3 py-1 text-[0.7rem] font-semibold tracking-[0.12em] text-moss-800">
      {tag}
    </span>
    <p className="mt-4 text-sm text-ink/60">{authors}</p>
    <h3 className="mt-1 font-display text-lg font-semibold leading-snug text-ink">{title}</h3>
    <p className="mt-1 text-sm italic text-ink/70">{journal}</p>
    <a
      href={doi}
      target="_blank"
      rel="noreferrer"
      className="mt-3 inline-block break-all text-sm font-medium text-moss-700 underline decoration-moss-300 underline-offset-4 transition-colors hover:text-moss-900"
    >
      {doi}
    </a>
  </article>
);

const Main = (props) => {
  const { setColorBg, setLogoHeader, setShowSpecies } = props;

  // Returning to the home page restores the neutral PlantaeViz chrome.
  useEffect(() => {
    if (setColorBg) setColorBg('bg-moss-900');
    if (setLogoHeader) setLogoHeader('/plantaeviz/PlantaeViz_home_header.svg');
    if (setShowSpecies) setShowSpecies(true);
  }, [setColorBg, setLogoHeader, setShowSpecies]);

  return (
    <div className="space-y-16">
      {/* Hero / introduction — full-bleed banner */}
      <section className="animate-fade-up">
        <div className="relative -mt-10 ml-[calc(50%_-_50vw)] w-screen overflow-hidden bg-gradient-to-br from-moss-900 to-moss-700 text-paper">
          <div
            className="pointer-events-none absolute -right-16 -top-16 h-64 w-64 rounded-full opacity-20"
            style={{ background: 'radial-gradient(circle, #cdddc4 0%, transparent 70%)' }}
          />
          <div className="relative mx-auto max-w-[1400px] px-5 py-12 sm:px-8 sm:py-16">
            <p className="max-w-8xl text-base leading-relaxed text-moss-100/90 sm:text-lg">
            The PlantaeViz platform is a web-based platform that offers several tools to visualize gene expression data in different transcriptomic datasets (microarray or RNA-Seq) related to the Plantae kingdom. You can also explore gene-co-expression networks, gene function associations, DAP-Seq data, and correlations between gene expression and metabolites. The platform is part of TOMSBiolab at the Institute of Integrative Systems Biology (I2SYSBIO, UV-CSIC). The platform also includes a search engine for looking correspondences between different genome annotations and Reference Catalogues for each of the species.
            </p>
          </div>
        </div>

        <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {features.map(({ icon: Icon, label, text }, i) => (
            <div
              key={label}
              className="animate-fade-up rounded-2xl border border-moss-200/70 bg-white p-5 shadow-soft"
              style={{ animationDelay: `${100 + i * 80}ms` }}
            >
              <Icon className="h-6 w-6 text-moss-700" />
              <h3 className="mt-3 font-display text-base font-semibold text-ink">{label}</h3>
              <p className="mt-1 text-sm text-ink/60">{text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Contact / feedback */}
      <section className="flex flex-col items-start gap-4 rounded-2xl border border-moss-200/70 bg-moss-50 p-6 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3">
          <Mail className="mt-0.5 h-5 w-5 shrink-0 text-moss-700" />
          <p className="text-sm leading-relaxed text-ink/80">
            Found a bug, or want to share feedback? Reach out to{' '}
            <strong className="text-ink">Antonio Santiago</strong> (antsanpaj&nbsp;[at]&nbsp;gmail.com)
            {' '}or <strong className="text-ink">David Navarro</strong> (david.navarro.paya&nbsp;[at]&nbsp;gmail.com).
          </p>
        </div>
      </section>

      {/* Citations */}
      <section>
        <div className="mb-7 flex items-center gap-4">
          <Search className="h-6 w-6 text-moss-700" />
          <div>
            <span className="text-[0.7rem] font-semibold uppercase tracking-[0.2em] text-moss-700">
              How to cite
            </span>
            <h2 className="font-display text-3xl font-semibold text-ink">References &amp; resources</h2>
          </div>
        </div>
        <div className="flex flex-col gap-5">
          {featuredCitations.map((c) => (
            <CitationCard key={c.tag} {...c} />
          ))}
          <div className="flex flex-col gap-5 lg:flex-row lg:items-start">
            <div className="flex flex-1 flex-col gap-5">
              {restCitations.filter((_, i) => i % 2 === 0).map((c) => (
                <CitationCard key={c.tag} {...c} />
              ))}
            </div>
            <div className="flex flex-1 flex-col gap-5">
              {restCitations.filter((_, i) => i % 2 === 1).map((c) => (
                <CitationCard key={c.tag} {...c} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Acknowledgements */}
      <section className="border-t border-moss-200/70 pt-10">
        <h2 className="mb-6 font-display text-2xl font-semibold text-ink">Acknowledgements</h2>
        <div className={styles.acknowledgement}>
          {acknowledgements.map((logo) => (
            <img
              key={logo.alt}
              src={logo.src}
              alt={logo.alt}
              style={{ height: logo.h }}
              className="w-auto opacity-80 grayscale transition-all duration-300 hover:opacity-100 hover:grayscale-0"
            />
          ))}
        </div>
      </section>
    </div>
  );
};

export default Main;
